<template>
  <v-layout
    id="ito-edit-form"
    justify-center
    wrap
  >
    <v-flex
      xs12
      md8
      lg8
    >
      <h2 v-if="!ito">
        {{ $t('modules.ito.new') }}
      </h2>
      <h2 v-else>
        {{ $t('modules.ito.edit') }}
      </h2>
      <v-card class="pt-7 pl-5 pr-5">
        <v-form>
          <v-layout wrap>
            <helper-cropper
              ref="cropper"
              :aspect-ratio="350 / 350"
              :width="350"
              :height="350"
              @updateAvatar="updateImage"
            />
            <v-flex
              mt-4
              xs12
            >
              <div
                class="image"
                @click.stop="$refs.image.click()"
              >
                <div
                  v-if="!form.icon"
                  class="pt-5 pb-7"
                >
                  <v-icon color="primary">
                    mdi-cloud-upload
                  </v-icon>
                  <p>
                    {{ $t('modules.ito.uploadIcon') }}
                  </p>
                  <p>PNG / JPG</p>
                </div>
                <div style="width:300px;overflow:hidden;">
                  <img
                    v-if="form.icon"
                    :src="form.icon"
                    width="150"
                  >
                </div>
                <input
                  ref="image"
                  type="file"
                  style="display: none"
                  accept="image/*"
                  @change="setImage"
                >
              </div>
              <div class="text-center mt-2">
                <v-btn
                  v-if="form.icon"
                  class="ml-2"
                  outlined
                  small
                  text
                  @click="form.icon = ''; $refs.image.value = '';"
                >
                  <v-icon>
                    mdi-bucket-outline
                  </v-icon>
                </v-btn>
              </div>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-text-field
                v-model.trim="form.token_symbol"
                :error-messages="TokenSymbolErrors"
                :disabled="!!ito"
                :label="$t('modules.ito.symbol')+ '*'"
                @input="$v.form.token_symbol.$touch()"
                @blur="$v.form.token_symbol.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-text-field
                v-model.trim="form.token_name"
                :counter="20"
                :error-messages="TokenNameErrors"
                :label="$t('modules.ito.name')+ '*'"
                @input="$v.form.token_name.$touch()"
                @blur="$v.form.token_name.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-select
                v-model.trim="form.status"
                :items="statuses"
                :item-text="'text'"
                :item-value="'id'"
                :error-messages="StatusErrors"
                :label="$t('modules.ito.status')+'*'"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-select
                v-model.trim="form.network"
                :items="networkList"
                :item-text="'name'"
                :item-value="'value'"
                :error-messages="NetworkErrors"
                :label="$t('modules.ito.network')+'*'"
              />
            </v-flex>
            <v-flex
              xs12
            >
              <v-text-field
                v-model.trim="form.smart_contract_address"
                :error-messages="SmartContractAddressErrors"
                :label="$t('modules.ito.smartContractAddress')+ '*'"
                @input="$v.form.smart_contract_address.$touch()"
                @blur="$v.form.smart_contract_address.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-text-field
                v-model.trim.number="form.current_price"
                :error-messages="CurrentPriceError"
                suffix="USDT"
                :disabled="!!ito"
                :label="$t('modules.ito.currentPrice')"
                @input="$v.form.current_price.$touch()"
                @blur="$v.form.current_price.$touch()"
                @keypress="onlyForCurrency($event, form.current_price)"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-text-field
                v-model.trim.number="form.next_price"
                :error-messages="NextPriceError"
                suffix="USDT"
                :disabled="!!ito"
                :label="$t('modules.ito.nextPrice')"
                @input="$v.form.next_price.$touch()"
                @blur="$v.form.next_price.$touch()"
                @keypress="onlyForCurrency($event, form.next_price)"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-text-field
                v-model.number="form.vesting_period_days"
                :error-messages="VestingPeriodError"
                :label="$t('modules.ito.vestingPeriod') + '*'"
                @input="$v.form.vesting_period_days.$touch()"
                @blur="$v.form.vesting_period_days.$touch()"
                @keypress="onlyForCurrency($event, form.vesting_period_days)"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-menu
                v-model="datePicker"
                :close-on-content-click="false"
                :nudge-right="120"
                top
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="formatedValidTillDate"
                    :label="$t('modules.ito.startDate')+'*'"
                    prepend-icon="mdi-calendar"
                    :error-messages="StartDateErrors"
                    readonly
                    v-on="on"
                    @input="$v.form.start_date.$touch()"
                    @blur="$v.form.start_date.$touch()"
                  />
                </template>
                <v-date-picker 
                  v-model="form.start_date" 
                  :locale="$i18n.locale"
                  :min="$moment().format('YYYY-MM-DD')"
                  @input="datePicker = false"
                />
              </v-menu>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-text-field
                v-model.number="form.market_cap"
                :error-messages="MarketCapError"
                :suffix="form.token_symbol"
                :label="$t('modules.ito.marketCap') + '*'"
                @input="$v.form.market_cap.$touch()"
                @blur="$v.form.market_cap.$touch()"
                @keypress="onlyForCurrency($event, form.market_cap)"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-text-field
                v-model.number="form.token_unlocked_percent"
                :error-messages="UnlockedPrecentError"
                suffix="%"
                :label="$t('modules.ito.tokenUnlocked') + '*'"
                @input="$v.form.token_unlocked_percent.$touch()"
                @blur="$v.form.token_unlocked_percent.$touch()"
                @keypress="onlyForCurrency($event, form.token_unlocked_percent)"
              />
            </v-flex>
            <v-flex
              xs12
            >
              <b>{{ $t("modules.ito.products") }}</b>
            </v-flex>
            <v-flex
              xs12
            >
              <products-i-t-o
                ref="products"
                :products="products"
                :edit-list="form.products_tokens"
                @saveProducts="saveProducts"
              />
            </v-flex>
            <v-flex
              xs12
              mt-3
            >
              <b>{{ $t("modules.ito.description") }}*</b>
            </v-flex>
            <v-flex>
              <vue-editor
                id="editor"
                v-model="form.token_description"
                :editor-toolbar="customToolbar"
                :editor-options="editorSettings"
                @text-change="$v.form.token_description.$touch()"
                @blur="$v.form.token_description.$touch()"
              />
              <small
                v-if="TokenDescriptionErrors.length > 0"
                class="text-negative"
              >{{ TokenDescriptionErrors[0] }}</small>
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex
              xs12
              md12
            >
              <v-btn
                fab
                outlined
                small
                text
                @click="$emit('closeEditMode', false)"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                style="float:right"
                :disabled="loading || ($v.$invalid)"
                :loading="loading"
                @click="save()"
              >
                {{ $t('globals.saveButton') }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import ITO_CREATE_MUTATION from "@/graphql/mutations/modules/ItoCreate.gql";
import ITO_UPDATE_MUTATION from "@/graphql/mutations/modules/ItoUpdate.gql";

import { validationMixin } from "vuelidate";
import {
  required,
  minLength,
  maxLength,
  numeric,
  decimal,
  minValue,
  maxValue,
} from "vuelidate/lib/validators";
import { VueEditor } from "vue2-editor";
const WAValidator = require('multicoin-address-validator');

import ProductsITO from "./patrial/ProductsITO";

const good_symbol = value => /^[A-Z\d\W]+$/.test(value);
const latin_symbol = value => /^[A-Z0-9]*$/.test(value)

const validateWallet = (address) => {
  if (!address) return true
  return WAValidator.validate(address, 'ETH')
}

export default {
  name: "ModuleITOEditForm",
  components: {
    VueEditor,
    ProductsITO
  },
  mixins: [validationMixin],
  props: {
    ito: {
      type: Object,
      default: null
    },
    products: {
      type: Array,
      default: null
    },
    whitelabelUuid: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      loading: false,
      datePicker: null,
      statuses: [
        {id: "hidden", text: this.$t('modules.ito.hidden')},
        {id: "active", text: this.$t('modules.ito.active')},
        {id: "archived", text: this.$t('modules.ito.archived')}
      ],
      networkList: [
        { name: 'Polygon', value: "polygon" },
      ], // ethereum,tron,bsc,polygon
      customToolbar: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': []}, 'link', 'image'],

        ['clean']
      ],
      editorSettings: {
        modules: {
          imageResize: {
            displaySize: true,
            imageDrop: true
          }
        }
      },
      form: {}
    }
  },
  computed: {
    rules() {
      return {
        token_symbol: {
          required,
          minLength: minLength(1),
          maxLength: maxLength(5),
          good_symbol,
          latin_symbol
        },
        token_name: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(20),
        },
        token_description: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(1000),
        },
        status: {
         required,
        },
        smart_contract_address: {
          required,
          validateWallet
        },
        network: {
         required,
        },
        current_price: {
          decimal,
          minValue: minValue(0),
          maxValue: maxValue(999999999)
        },
        next_price: {
          decimal,
          minValue: minValue(0),
          maxValue: maxValue(999999999)
        },
        vesting_period_days: {
          required,
          numeric,
          minValue: minValue(0),
          maxValue: maxValue(365)
        },
        start_date: {
          required
        },
        market_cap: {
          required,
          decimal,
          minValue: minValue(0),
          maxValue: maxValue(999999999)
        },
        token_unlocked_percent: {
          required,
          decimal,
          minValue: minValue(0),
          maxValue: maxValue(100)
        }
      }
    },
    TokenNameErrors() {
      const errors = [];
      if (!this.$v.form.token_name.$dirty) return errors;
      !this.$v.form.token_name.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.token_name.minLength &&
        errors.push(this.$t("globals.minError", {char: this.$v.form.token_name.$params.minLength.min}));
      !this.$v.form.token_name.maxLength &&
        errors.push(this.$t("globals.maxError", {char: this.$v.form.token_name.$params.maxLength.max}));
      return errors;
    },
    TokenSymbolErrors() {
      const errors = [];
      if (!this.$v.form.token_symbol.$dirty) return errors;
      !this.$v.form.token_symbol.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.token_symbol.minLength &&
        errors.push(this.$t("globals.minError", {char: this.$v.form.token_symbol.$params.minLength.min}));
      !this.$v.form.token_symbol.maxLength &&
        errors.push(this.$t("globals.maxError", {char: this.$v.form.token_symbol.$params.maxLength.max}));
      !this.$v.form.token_symbol.good_symbol &&
        errors.push(this.$t('modules.ito.mustUppercase'))
      !this.$v.form.token_symbol.latin_symbol &&
        errors.push(this.$t('modules.ito.onlyLatin'))
      return errors;
    },
    StatusErrors(value) {
      const errors = [];
      if (!this.$v.form.status.$dirty) return errors;
      !this.$v.form.status.required && errors.push(this.$t("globals.reqField"));
      return errors;
    },
    SmartContractAddressErrors() {
      const errors = [];
      if (!this.$v.form.smart_contract_address.$dirty) return errors;
      !this.$v.form.smart_contract_address.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.smart_contract_address.validateWallet &&
        errors.push(this.$t("modules.stakingContractNotValid"));
      return errors;
    },
    NetworkErrors() {
      const errors = [];
      if (!this.$v.form.network.$dirty) return errors;
      !this.$v.form.network.required && errors.push(this.$t("globals.reqField"));
      return errors;
    },
    CurrentPriceError(){
      const errors = [];
      if (!this.$v.form.current_price.$dirty) return errors;
      !this.$v.form.current_price.decimal &&
        errors.push(this.$t("globals.numberField"));
      !this.$v.form.current_price.minValue &&
        errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.current_price.$params.minValue.min}));
      !this.$v.form.current_price.maxValue &&
        errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.current_price.$params.maxValue.max}));
      return errors;
    },
    NextPriceError(){
      const errors = [];
      if (!this.$v.form.next_price.$dirty) return errors;
      !this.$v.form.next_price.decimal &&
        errors.push(this.$t("globals.numberField"));
      !this.$v.form.next_price.minValue &&
        errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.next_price.$params.minValue.min}));
      !this.$v.form.next_price.maxValue &&
        errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.next_price.$params.maxValue.max}));
      return errors;
    },
    VestingPeriodError(){
      const errors = [];
      if (!this.$v.form.vesting_period_days.$dirty) return errors;
      !this.$v.form.vesting_period_days.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.vesting_period_days.numeric &&
        errors.push(this.$t("globals.mustBeNumber"));
      !this.$v.form.vesting_period_days.minValue &&
        errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.vesting_period_days.$params.minValue.min}));
      !this.$v.form.vesting_period_days.maxValue &&
        errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.vesting_period_days.$params.maxValue.max}));
      return errors;
    },
    StartDateErrors() {
      const errors = [];
      if (!this.$v.form.start_date.$dirty) return errors;
      !this.$v.form.start_date.required && errors.push(this.$t("globals.reqField"));
      return errors;
    },
    MarketCapError(){
      const errors = [];
      if (!this.$v.form.market_cap.$dirty) return errors;
      !this.$v.form.market_cap.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.market_cap.decimal &&
        errors.push(this.$t("globals.numberField"));
      !this.$v.form.market_cap.minValue &&
        errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.market_cap.$params.minValue.min}));
      !this.$v.form.market_cap.maxValue &&
        errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.market_cap.$params.maxValue.max}));
      return errors;
    },
    UnlockedPrecentError(){
      const errors = [];
      if (!this.$v.form.token_unlocked_percent.$dirty) return errors;
      !this.$v.form.token_unlocked_percent.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.token_unlocked_percent.decimal &&
        errors.push(this.$t("globals.numberField"));
      !this.$v.form.token_unlocked_percent.minValue &&
        errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.market_cap.$params.minValue.min}));
      !this.$v.form.token_unlocked_percent.maxValue &&
        errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.market_cap.$params.maxValue.max}));
      return errors;
    },
    TokenDescriptionErrors(value) {
      const errors = [];
      if (!this.$v.form.token_description.$dirty) return errors;
      !this.$v.form.token_description.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.token_description.minLength &&
        errors.push(this.$t("globals.minError", {char: this.$v.form.token_description.$params.minLength.min}));
      !this.$v.form.token_description.maxLength &&
        errors.push(this.$t("globals.maxError", {char: this.$v.form.token_description.$params.maxLength.max}));
      return errors;
    },
    formatedValidTillDate() {
      return this.$moment(this.form.start_date).format('ll');
    },
  },
  mounted() {
    const form = JSON.parse(JSON.stringify(this.ito));
    if (form) {
      this.form = {
        ito_uuid: form.uuid,
        products_tokens: [],
        ...form
      }
    } else {
      this.form = {
        icon: "",
        token_symbol: "",
        token_name: "",
        token_description: "",
        status: "",
        smart_contract_address: "",
        network: "",
        current_price: 0,
        next_price: 0,
        products_tokens: [],
        vesting_period_days: 0,
        start_date: "",
        market_cap: 0,
        token_unlocked_percent: 0,
        vesting_period_days: 0,
        start_date: this.$moment().format('YYYY-MM-DD'),
        market_cap: 0,
        token_unlocked_percent: 0
      }
    }
  },
  validations() {
    return {
      form: this.rules
    };
  },
  methods: {
    setImage(e) {
      if (!this.$refs.cropper.setImage(e)) {
        this.$refs.image.value = "";
      }
    },
    updateImage(image) {
      this.form.icon = image;
    },
    saveProducts(products) {
      this.form.products_tokens = products;
    },
    async save() {
      this.$refs.products.$v.$touch();
      this.$refs.products.save();
      this.$v.$touch();
      if (this.$v.$invalid || this.$refs.products.$v.$invalid) {
        return
      }
      this.loading = true;
      this.form.whitelabel_uuid = this.whitelabelUuid;
      this.form.current_price = this.form.current_price || 0;
      this.form.vesting_period_days = this.form.vesting_period_days || 0;
      this.form.market_cap = this.form.market_cap || 0;
      this.form.token_unlocked_percent = this.form.token_unlocked_percent || 0;
      this.form.next_price = this.form.next_price || null;

      if(this.ito) {
        delete this.form.current_price;
        delete this.form.next_price;
        delete this.form.token_symbol
      }

      this.$apollo
        .mutate({
          mutation: !this.ito ? ITO_CREATE_MUTATION : ITO_UPDATE_MUTATION,
          variables: this.form
        })
        .then(data => {
          this.$emit('closeEditMode', true)
        })
        .catch(error => {
          this.errorHandler(error);
          this.loading = false;
        });
    }
  },
}
</script>

<style lang="scss">
#editor {
  height: 400px;
}
#ito-edit-form {
  .image {
    width: 306px;
    margin: 0 auto;
    /* border: 3px solid; */
    border-radius: 5px;
    text-align: center;
    cursor: pointer;
    i {
      margin: 5px 0 0px;
      font-size: 26px;
    }
    p {
      margin: 7px 0 0!important;
      &:first-of-type {
        font-weight: 700;
      }
      line-height: 0.85rem;
    }
    small {
      font-size: 11px;
    }
    &.no-border {
      border: 0 !important;
    }
  }
}
</style>
